import type { EntityImageInputPayload } from "api/entities";
import { uploadImage } from "api/images";
import type { EntityImageDto } from "api/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { compressImage } from "utilities";

import type { GalleryImage, UseEntityImagesOptions } from "./entityImages.types";

const newTempId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const toGalleryImages = (images: EntityImageDto[]): GalleryImage[] =>
	[...images]
		.sort((a: EntityImageDto, b: EntityImageDto) => a.sortOrder - b.sortOrder)
		.map((image: EntityImageDto) => ({
			id: image.id,
			url: image.url,
			name: image.name,
			isDefault: image.isDefault
		}));

export type UseEntityImages = {
	images: GalleryImage[];
	busy: boolean;
	failed: boolean;
	addFiles: (files: File[]) => Promise<void>;
	removeImage: (id: string) => void;
	setDefault: (id: string) => void;
	/** The gallery to send in the create/update payload (persisted only when the form is saved). */
	toInputPayload: () => EntityImageInputPayload[];
};

/**
 * Buffered image-gallery editor. Every change (add / remove / set-default) lives in local state and
 * is persisted only when the parent form is saved — the create/update request carries
 * `toInputPayload()`. This means unsaved edits are discarded on cancel or reload. New files are
 * compressed to ≤ ~100 KB and uploaded to blob storage up front so their URL is ready for the save.
 */
export const useEntityImages = ({
	entityId,
	initialImages,
	getAccessToken
}: UseEntityImagesOptions): UseEntityImages => {
	const [images, setImages] = useState<GalleryImage[]>(() => toGalleryImages(initialImages ?? []));
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState(false);
	const seededKey = useRef<string | null>(entityId);

	// Re-seed if the target entity changes (defensive — dialogs normally mount fresh per entity).
	useEffect(() => {
		if (seededKey.current !== entityId) {
			seededKey.current = entityId;
			setImages(toGalleryImages(initialImages ?? []));
			setFailed(false);
		}
	}, [entityId, initialImages]);

	const addFiles = useCallback(
		async (files: File[]): Promise<void> => {
			if (files.length === 0) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setBusy(true);
			setFailed(false);
			try {
				// Uploads run one at a time on purpose: keeps ordering stable and avoids a burst of
				// parallel compress+upload work for a multi-file selection.
				/* eslint-disable no-await-in-loop */
				for (const file of files) {
					try {
						const compressed = await compressImage(file);
						const uploaded = await uploadImage(compressed, "entities", token);
						setImages((current: GalleryImage[]) => [
							...current,
							{
								id: newTempId(),
								url: uploaded.url,
								name: uploaded.name,
								isDefault: current.length === 0
							}
						]);
					} catch {
						setFailed(true);
					}
				}
				/* eslint-enable no-await-in-loop */
			} finally {
				setBusy(false);
			}
		},
		[getAccessToken]
	);

	const removeImage = useCallback((id: string): void => {
		setImages((current: GalleryImage[]) => {
			const target = current.find((image: GalleryImage) => image.id === id);
			const next = current.filter((image: GalleryImage) => image.id !== id);
			// Promote a new default when the removed image was the default one.
			if (target?.isDefault && next.length > 0 && !next.some((image: GalleryImage) => image.isDefault)) {
				next[0] = { ...next[0], isDefault: true };
			}

			return next;
		});
	}, []);

	const setDefault = useCallback((id: string): void => {
		setImages((current: GalleryImage[]) =>
			current.map((image: GalleryImage) => ({ ...image, isDefault: image.id === id }))
		);
	}, []);

	const toInputPayload = useCallback(
		(): EntityImageInputPayload[] =>
			images.map((image: GalleryImage) => ({
				url: image.url,
				name: image.name ?? undefined,
				isDefault: image.isDefault
			})),
		[images]
	);

	return { images, busy, failed, addFiles, removeImage, setDefault, toInputPayload };
};
